import 'regenerator-runtime/runtime';
import PropTypes from 'prop-types';
import { withManifest } from '@godaddy/gasket-hcs';
import React, { useState, useMemo, useCallback, useRef } from 'react';
import SidebarRoot from './header/src/header';

// imported styles specifically for skip navigation
import '@godaddy/gasket-hcs/styles.css';

export default function Header(props) {
  const propsRef = useRef();
  propsRef.current = props;

  const [helpUrl, setHelpUrl] = useState(null);
  const [navHeading, setNavHeading] = useState(null);
  const [navSubHeadline, setNavSubHeadline] = useState(null);
  const [navBreadcrumb, setNavBreadcrumb] = useState(null);
  const [afterNavHeadingLeft, setAfterNavHeadingLeft] = useState(null);
  const [beforeNavHeading, setBeforeNavHeading] = useState(null);
  const [afterNavHeadingRight, setAfterNavHeadingRight] = useState(null);
  const [sidebarNav, setSidebarNav] = useState(props.sidebarNav);
  const [accountTrayNav, setAccountTrayNav] = useState(props.accountTrayNav);
  const [accountTrayBottomNav, setAccountTrayBottomNav] = useState(null);
  const [navigationTop, setNavigationTop] = useState(props.navigationTop);
  const [sidebarFooterContent, setSidebarFooterContent] = useState(props.sidebarFooterContent);
  const [pollNotificationsTimeout, setPollNotificationsTimeout] = useState(props.pollNotificationsTimeout);
  const [reloadNotificationsCounter, setReloadNotificationsCounter] = useState(0);
  const [sidebarNavLinkHandler, setSidebarNavLinkHandler] = useState(null);
  const [customNotifications, setCustomNotifications] = useState([]);
  const [cartItems, setCartItems] = useState(0);
  const [CartComponent, setCartComponent] = useState('');
  const [cartLink, setCartLink] = useState(null);
  const [customWaffleLinks, setCustomWaffleLinks] = useState(null);
  const [sidebarNavTopComponent, setSidebarNavTopComponent] = useState(null);
  const [inPageNavContainerClass, setInPageNavContainerClass] = useState(null);
  const [primaryActiveNavLink, setPrimaryActiveNavLink] = useState(null);

  /**
   * Helper to update navigations (regular or right sided). Clones object or arrays
   * to ensure setState is done against a new object in memory.
   *
   * @param {React.ReactElement} url updated help url.
   * @param {Function} done Optional completion callback.
   * @public
   */
  const updateHelpUrl = useCallback((helpUrlInput, target = '_self', done = () => { }) => {
    setHelpUrl({ href: helpUrlInput, target });
    done();
  }, [setHelpUrl]);

  /**
   * Helper to update venture switcher
   *
   * @param {React.ReactElement} sidebarNavTopComponent Updated with new component.
   * @returns {Self} fluent interface.
   * @public
   */
  const updateSidebarNavTopComponent = useCallback((sidebarNavTopComponent) => {
    // eslint-disable-next-line no-process-env
    if (typeof sidebarNavTopComponent !== 'function' && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('Parameter not valid React component');
    } else {
      setSidebarNavTopComponent(sidebarNavTopComponent);
    }
  }, [setSidebarNavTopComponent]);

  /**
   * Helper to update sidebar nav
   *
   * @param {array|Object|function} newNav Updated navigation, can be navigation array or object.
   * @returns {Self} fluent interface.
   * @public
   */
  const updateSidebarNav = useCallback((newNav = []) => {
    if (Array.isArray(newNav)) {
      setSidebarNav(newNav);
    } else {
      const updatedNav = newNav(propsRef.current.sidebarNav);
      if (Array.isArray(updatedNav)) {
        setSidebarNav(updatedNav);
      }
    }
  }, [setSidebarNav, propsRef]);

  /**
  * Helper to reload notifications
  *
  * @returns {Self} fluent interface.
  * @public
  */
  const reloadNotifications = useCallback(() => {
    setReloadNotificationsCounter((count) => count + 1);
  }, [setReloadNotificationsCounter]);

  /**
   * Helper to update top navigation
   *
   * @param {array|React.ReactElement} newNav Updated navigation, can be navigation array or component.
   * @returns {Self} fluent interface.
   * @public
   */
  const updateNavigationTop = useCallback((newNav = []) => {
    if ((newNav && Array.isArray(newNav)) || React.isValidElement(newNav)) {
      setNavigationTop(newNav);
    }
  }, [setNavigationTop]);

  /**
   * Helper to update navigation heading
   *
   * @param {any[]|React.ReactElement} item Updated navigation, can be a object
   * @returns {boolean} true if the item is an object, false otherwise
   * @private
   */
  function isObject(item) {
    return item != null && typeof item === 'object' && !Array.isArray(item);
  }

  /**
   * @typedef {Object} HeadingData
   * @prop {string} text Text of heading
   */
  /**
   * Helper to update account navigation heading
   *
   * @param {HeadingData} heading Data to use for heading
   * @public
   */
  const updateNavHeading = useCallback((heading = {}) => {
    if (isObject(heading)) {
      setNavHeading(heading);
    }
  }, [setNavHeading]);

  /**
   * Helper to update space before the headline on the left side
   *
   * @param {React.ReactElement} beforeHeadingLeft Updated left of inpage nav Headline can be component.
   * @returns {Self} fluent interface.
   * @public
   */
  const updateBeforeNavHeading = useCallback((beforeHeading) => {
    if (React.isValidElement(beforeHeading) || beforeHeading == null) {
      setBeforeNavHeading(beforeHeading);
    }
  }, [setBeforeNavHeading]);

  /**
   * Helper to update space after the headline on the left side
   *
   * @param {React.ReactElement} afterHeadingLeft Updated left of inpage nav Headline can be component.
   * @returns {Self} fluent interface.
   * @public
   */
  const updateAfterNavHeadingLeft = useCallback((afterHeadingLeft) => {
    if (React.isValidElement(afterHeadingLeft) || afterHeadingLeft == null) {
      setAfterNavHeadingLeft(afterHeadingLeft);
    }
  }, [setAfterNavHeadingLeft]);

  /**
   * Helper to update space on the right side of the headline
   *
   * @param {React.ReactElement} headingRight Updated left of inpage nav Headline can be component.
   * @returns {Self} fluent interface.
   * @public
   */
  const updateAfterNavHeadingRight = useCallback((headingRight) => {
    if (React.isValidElement(headingRight) || headingRight == null) {
      setAfterNavHeadingRight(headingRight);
    }
  }, [setAfterNavHeadingRight]);

  /**
   * @typedef {Object} SubHeadlineData
   * @prop {string} subheadline Text of subheading
   */
  /**
   * Helper to update account navigation subheading
   *
   * @param {SubHeadlineData} subheadline Data to use for subheading
   * @public
   */
  const updateNavSubHeadline = useCallback((subheadline = {}) => {
    if (isObject(subheadline) || React.isValidElement(subheadline)) {
      setNavSubHeadline(subheadline);
    }
  }, [setNavSubHeadline]);

  /**
   * @typedef {Object} BreadcrumbData
   * @prop {string} Breadcrumb Data of Breadcrumb
   */
  /**
   * Helper to update backlink or breadcrumb
   *
   * @param {breadcrumbData} breadcrumb data updated breadcrumb or backlink.
   * @public
   */
  const updateNavBreadcrumb = useCallback((breadcrumbData = {}) => {
    if (isObject(breadcrumbData)) {
      setNavBreadcrumb(breadcrumbData);
    }
  }, [setNavBreadcrumb]);

  /**
   * Update the in-page nav wrapper to include a custom CSS class
   *
   * @param {string} newClass Custom CSS class
   * @public
   */
  const updateInPageNavContainerClass = useCallback((newClass) => {
    setInPageNavContainerClass(newClass);
  }, [setInPageNavContainerClass]);

  /**
   * Helper to update account-tray nav
   *
   * @param {Object[]|Object} newNav Updated navigation, can be navigation array or object.
   * @public
   */
  const updateAccountTrayNav = useCallback((newNav = []) => {
    if (Array.isArray(newNav)) {
      setAccountTrayNav(newNav);
    } else {
      const updatedNav = newNav(propsRef.current.accountTrayNav);
      if (Array.isArray(updatedNav)) {
        setAccountTrayNav(updatedNav);
      }
    }
  }, [setAccountTrayNav, propsRef]);

  /**
   * Helper to update account-tray bottom nav
   *
   * @param {array|React.ReactElement} newNav Updated navigation, can be navigation array or component.
   * @returns {Self} fluent interface.
   * @public
   */
  const updateAccountTrayBottomNav = useCallback((newNav = []) => {
    if ((newNav && Array.isArray(newNav)) || React.isValidElement(newNav)) {
      setAccountTrayBottomNav(newNav);
    }
  }, [setAccountTrayBottomNav]);

  /**
   * Helper to update content in sidebar footer
   *
   * @param {Object[]|Object} newContent Updated sidebar footer content,
   * can be navigation array or function that returns new content array
   * @public
   */
  const updateSidebarFooter = useCallback((newContent = []) => {
    if (Array.isArray(newContent)) {
      setSidebarFooterContent(newContent);
    } else {
      const updatedContent = newContent(propsRef.current.sidebarFooterContent);
      if (Array.isArray(updatedContent)) {
        setSidebarFooterContent(updatedContent);
      }
    }
  }, [setSidebarFooterContent, propsRef]);

  /**
   * Helper to register links in app-sidebar
   *
   * @returns {Self|func} should return handler or fluent interface.
   * @public
   */
  const onNavLinkClick = useCallback((handlerFn) => {
    if (handlerFn && handlerFn instanceof Function) {
      setSidebarNavLinkHandler(handlerFn);
    }
  }, [setSidebarNavLinkHandler]);

  /**
  * Helper to override Primary Active Nav
  * @param {string} href of link designated to be active
  * @returns {Self} fluent interface.
  * @public
  */
  const updatePrimaryActiveNavLink = useCallback((activePrimaryNavHref) => {
    if (activePrimaryNavHref && (typeof activePrimaryNavHref === 'string')) {
      setPrimaryActiveNavLink(activePrimaryNavHref);
    }
  }, [setPrimaryActiveNavLink]);

  /**
   * Helper to update notifications. To allow further customization inside the notification bell
   * below the 'standard/global' notifications
   *
   * @param {array|React.ReactElement} newNotification Custom notification to be added.
   * @returns {Self}
   *
   * @public
   */
  const addCustomNotifications = useCallback((newNotif = []) => {
    if (newNotif && Array.isArray(newNotif)) {
      setCustomNotifications(newNotif);
    }
  }, [setCustomNotifications]);

  /**
   * Helper to update cart items.
   *
   * @param {number} cartItems Updated cart count.
   * @param {function} done Optional completion callback.
   * @public
   */
  const updateCart = useCallback((cartItems, done = () => { }) => {
    cartItems = parseInt(cartItems, 10) || 0;
    setCartItems(cartItems);
    done();
  }, [setCartItems]);

  /**
   * Helper to update cart component.
   *
   * @param {React.ReactElement} component Updated React component.
   * @param {Function} done Continuation to call when complete.
   * @api public
   */
  const updateCartComponent = useCallback((CartComponent, done = () => { }) => {
    setCartComponent(CartComponent);
    done();
  }, [setCartComponent]);

  /**
   * Helper to update cart checkout url/link.
   *
   * @param {React.ReactElement} url Updated cart url.
   * @param {Function} done Continuation to call when complete.
   * @api public
   */
  const updateCartLink = useCallback((cartLink, done = () => { }) => {
    setCartLink(cartLink);
    done();
  }, [setCartLink]);

  /**
   * Helper to update cart checkout url/link.
   *
   * @param {React.ReactElement} url Updated cart url.
   * @param {Function} done Continuation to call when complete.
   * @api public
   */
  const updateCartUrl = useCallback((cartLink, done = () => { }) => {
    setCartLink(cartLink);
    done();
  }, [setCartLink]);

  /**
   * Helper to send in custom waffle menu (App Switcher) links
   *
   * @param {Object} customLinks Updated waffle menu links
   * @returns {Self} fluent interface.
   * @public
   */
  const updateWaffleLinks = useCallback(customLinks => {
    if (customLinks && (Array.isArray(customLinks.topLinks) || Array.isArray(customLinks.quickLinks))) {
      setCustomWaffleLinks(customLinks);
    }
  }, [setCustomWaffleLinks]);

  const headerMethods = useMemo(() => ({
    updateHelpUrl,
    updateSidebarNav,
    updateAccountTrayNav,
    updateAccountTrayBottomNav,
    updateCart,
    updateCartComponent,
    updateCartLink,
    updateCartUrl,
    updateNavigationTop,
    updateSidebarFooter,
    updateNavHeading,
    updateNavSubHeadline,
    updateNavBreadcrumb,
    updateAfterNavHeadingLeft,
    updateBeforeNavHeading,
    updateAfterNavHeadingRight,
    updateInPageNavContainerClass,
    updatePollNotificationsTimeout: setPollNotificationsTimeout,
    reloadNotifications,
    onNavLinkClick,
    updatePrimaryActiveNavLink,
    addCustomNotifications,
    updateWaffleLinks,
    updateSidebarNavTopComponent
  }), [updateHelpUrl,
    updateSidebarNav,
    updateAccountTrayNav,
    updateAccountTrayBottomNav,
    updateCart,
    updateCartComponent,
    updateCartLink,
    updateCartUrl,
    updateNavigationTop,
    updateSidebarFooter,
    updateNavHeading,
    updateNavSubHeadline,
    updateNavBreadcrumb,
    updateAfterNavHeadingLeft,
    updateBeforeNavHeading,
    updateAfterNavHeadingRight,
    updateInPageNavContainerClass,
    setPollNotificationsTimeout,
    reloadNotifications,
    onNavLinkClick,
    updatePrimaryActiveNavLink,
    addCustomNotifications,
    updateWaffleLinks,
    updateSidebarNavTopComponent
  ]);

  const HeaderComponent = useMemo(() => withManifest(SidebarRoot, {
    renderAccountDelegation: props.accountDelegationBanner,
    initCustomerState: true,
    initTraffic: true,
    additionalHeaderMethods: headerMethods,
    componentName: 'header'
  }), [headerMethods]);

  return (
    <HeaderComponent { ...props }
      helpUrl={ helpUrl }
      sidebarNav={ sidebarNav }
      accountTrayNav={ accountTrayNav }
      accountTrayBottomNav={ accountTrayBottomNav }
      navigationTop={ navigationTop }
      sidebarFooterContent={ sidebarFooterContent }
      navHeading={ navHeading }
      navSubHeadline={ navSubHeadline }
      navBreadcrumb={ navBreadcrumb }
      afterNavHeadingLeft={ afterNavHeadingLeft }
      beforeNavHeading={ beforeNavHeading }
      afterNavHeadingRight={ afterNavHeadingRight }
      pollNotificationsTimeout={ pollNotificationsTimeout }
      reloadNotificationsCounter={ reloadNotificationsCounter }
      sidebarNavLinkHandler={ sidebarNavLinkHandler }
      primaryActiveNavLink={ primaryActiveNavLink }
      customNotifications={ customNotifications }
      cartItems={ cartItems }
      CartComponent={ CartComponent }
      cartLink={ cartLink }
      customWaffleLinks={ customWaffleLinks }
      sidebarNavTopComponent={ sidebarNavTopComponent }
      inPageNavContainerClass={ inPageNavContainerClass }
    />
  );
}

Header.propTypes = {
  accountDelegationBanner: PropTypes.bool,
  helpUrl: PropTypes.shape({
    href: PropTypes.string,
    target: PropTypes.string
  }),
  customNotifications: PropTypes.arrayOf(PropTypes.object),
  urls: PropTypes.shape({}),
  accountTrayNav: PropTypes.arrayOf(
    PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func
      ])
    )
  ),
  accountTrayBottomNav: PropTypes.node,
  sidebarNav: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      text: PropTypes.string
    })
  ])),
  navigationTop: PropTypes.node,
  sidebarFooterContent: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),
  pollNotificationsTimeout: PropTypes.number,
  cartItems: PropTypes.number,
  customWaffleLinks: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.objectOf(PropTypes.string)
    )
  ),
  sidebarNavTopComponent: PropTypes.node
};
